/**
 * The end of the queue: what the trainer just did, and the way to take back the last of it.
 *
 * ── The celebration belongs to an ACT DONE HERE ──────────────────────────────────────────────
 * A queue that was already empty on arrival is TOLD — `EmptyState`'s `positive` sentence, no
 * tallies, no congratulation — because congratulating somebody for a list that was empty before
 * they opened it is noise. This component only ever draws the reward when the trainer cleared the
 * deck in this sitting, which is the same rule `pages/trainer-home/model/queueClosing` states for
 * the bento, and `pages/tasks/ui/TasksScreen` is where the two cases are told apart.
 *
 * **Nothing about it is written down.** No `localStorage`, no flag on the server: a record from an
 * older build would have to be read defensively, and the reward is about *this* act anyway. After a
 * reload the deck is seeded from the server, comes back empty, and the screen simply says so.
 */

import { useTranslation } from 'react-i18next';

import { EmptyState } from '~/shared/ui/EmptyState';
import { DoneMarkIcon } from '~/shared/ui/EmptyStateIcons';
import ghost from '~/shared/ui/ghostButton.module.css';

import type { DeckTally } from '../model/taskDeck';
import { UndoIcon } from './TaskIcons';
import styles from './queueDone.module.css';

interface QueueDoneProps {
  readonly tally: DeckTally;
  /** `null` when nothing is held any more — there is then nothing to take back. */
  readonly onUndo: (() => void) | null;
}

export function QueueDone({ tally, onUndo }: QueueDoneProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.done}>
      <EmptyState
        mark={<DoneMarkIcon />}
        tone="positive"
        title={t('web:tasks.done.title')}
        hint={t('web:tasks.done.lede')}
      />
      <div className={styles.tally}>
        <div className={styles.tallyCard}>
          <b className={styles.tallyFigure}>{tally.done}</b>
          <span className={styles.tallyLabel}>{t('web:tasks.done.tallyDone')}</span>
        </div>
        <div className={styles.tallyCard}>
          <b className={styles.tallyFigure}>{tally.snoozed}</b>
          <span className={styles.tallyLabel}>{t('web:tasks.done.tallySnoozed')}</span>
        </div>
      </div>
      {onUndo === null ? null : (
        <button type="button" className={`${ghost.ghost} ${styles.undo}`} onClick={onUndo}>
          <UndoIcon className={styles.undoGlyph} />
          {t('web:tasks.done.undoLast')}
        </button>
      )}
    </div>
  );
}
